import { priceString } from "../../../utils/string";
import moment from "moment";
import ArtistAvatar from "../../../components/ArtistAvatar";
import { useNavigate } from "react-router-dom";

interface AuctionRecordListItemProps {
  item: any;
}

const AuctionRecordListItem: React.FC<AuctionRecordListItemProps> = ({
  item,
}) => {
  const navigate = useNavigate();

  return (
    <div
      key={item.id}
      className="bg-white border border-gallery-200 rounded-sm overflow-hidden hover:border-gallery-400 transition-colors"
    >
      <div className="flex flex-col md:flex-row">
        {/* Image */}
        <div
          className="w-full md:w-48 h-48 md:h-auto bg-gallery-100 flex items-center justify-center shrink-0 cursor-pointer"
          onClick={() => navigate(`/auction-record/${item._id}`)}
        >
          {item.medium_image_url ? (
            <img
              src={item.medium_image_url}
              alt={item.title}
              className="w-full h-full object-contain p-2"
            />
          ) : (
            <div className="text-gallery-400">
              <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 p-5 min-w-0">
          {/* Title & Artist Row */}
          <div className="mb-4">
            <h3
              onClick={() => navigate(`/auction-record/${item._id}`)}
              className="font-medium text-gallery-900 hover:text-gallery-600 cursor-pointer transition-colors truncate"
            >
              {item.title}
            </h3>
            <div
              onClick={() => navigate(`/artist/${item.artist._id || item.artist.id}`)}
              className="flex items-center gap-2 mt-1.5 cursor-pointer hover:opacity-80 transition-opacity w-fit"
            >
              <ArtistAvatar artist={item.artist} size="sm" />
              <span className="text-sm text-gallery-600">{item.artist.name}</span>
            </div>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-sm">
            {item.medium && (
              <div>
                <span className="text-gallery-400">Medium</span>
                <p className="text-gallery-700 truncate">{item.medium}</p>
              </div>
            )}
            {item.size && (
              <div>
                <span className="text-gallery-400">Dimensions</span>
                <p className="text-gallery-700">{item.size}</p>
              </div>
            )}
            {item.category && (
              <div>
                <span className="text-gallery-400">Category</span>
                <p className="text-gallery-700 capitalize">{item.category}</p>
              </div>
            )}
            {item.sale_date && (
              <div>
                <span className="text-gallery-400">Sale Date</span>
                <p className="text-gallery-700">{moment(item.sale_date).format("MMM YYYY")}</p>
              </div>
            )}
          </div>
        </div>

        {/* Price Column */}
        <div className="flex flex-row md:flex-col items-center justify-between md:justify-center gap-4 p-5 md:w-48 bg-gallery-50 border-t md:border-t-0 md:border-l border-gallery-100 shrink-0">
          <div className="text-center">
            {item.usd_hammer_price ? (
              <>
                <p className="font-display text-xl text-gallery-900">
                  {priceString(item.usd_hammer_price / 100)}
                </p>
                <p className="text-xs text-gallery-500 mt-0.5">Hammer Price</p>
              </>
            ) : (
              <p className="text-sm text-gallery-400">Price not available</p>
            )}

            {item.usd_estimate_low && item.usd_estimate_high && (
              <p className="text-xs text-gallery-400 mt-2">
                Est. {priceString(item.usd_estimate_low / 100)} - {priceString(item.usd_estimate_high / 100)}
              </p>
            )}
          </div>

          <button
            onClick={() => navigate(`/auction-record/${item._id}`)}
            className="text-xs font-medium text-gallery-600 hover:text-gallery-900 transition-colors flex items-center gap-1"
          >
            View Details
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Auction House Footer */}
      {item.auction?.auction_house?.name && (
        <div className="px-5 py-2.5 bg-gallery-50 border-t border-gallery-100 flex items-center justify-between">
          <span className="text-xs text-gallery-500">
            {item.auction.auction_house.name}
          </span>
          {item.price_per_cm_squared && (
            <span className="text-xs text-gallery-500">
              ${(item.price_per_cm_squared / 100).toFixed(2)}/cm²
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default AuctionRecordListItem;
